// Endpoint detail (doc 03 §3.2): status, hardware tab with inventory history,
// config tab with NIC role assignment, one-shot commands, and history.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router-dom";

import {
  api,
  type Channel,
  type Command,
  type EndpointDetail as Detail,
  type Me,
  type Page,
  type Site,
  type SourceHealth,
} from "../api";
import EndpointVitals, { faultsOf, type Telemetry } from "./EndpointVitals";
import ProvisionWizard, { OutputsStep, type OutputSpec } from "./ProvisionWizard";
import {
  Badge,
  Card,
  ConfirmButton,
  Empty,
  ErrorNote,
  Field,
  Metric,
  Modal,
  PageHeader,
  Spinner,
  StatusDot,
  endpointTone,
  formatAgo,
  formatDuration,
  formatTime,
  useAction,
} from "../ui";

type Tab = "status" | "vitals" | "hardware" | "config" | "commands" | "history";

export default function EndpointDetail({ me }: { me: Me }) {
  const { id = "" } = useParams();
  const [tab, setTab] = useState<Tab>("status");
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery<Detail>({
    queryKey: ["endpoint", id],
    queryFn: () => api.get(`/v1/endpoints/${id}`),
    refetchInterval: 30_000,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["endpoint", id] });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;
  if (!data) return null;

  const offline = data.state === "active" && !data.online;
  const telemetry = data.health_json.telemetry as Telemetry | undefined;
  // Counted here rather than inside the tab so the badge is visible from every
  // other tab — a failing DIMM should not need to be looked for.
  const faults = telemetry ? faultsOf(telemetry) : [];
  const worstFault = faults.some((f) => f.tone === "crit") ? "crit" : faults.length ? "warn" : null;

  const tabs: [Tab, string][] = [
    ["status", "Status"],
    ["vitals", "Vitals"],
    ["hardware", "Hardware"],
    ["config", "Config"],
    ["commands", "Commands"],
    ["history", "History"],
  ];

  return (
    <>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <span className="font-mono uppercase tracking-wider text-brand-on">{data.service_tag}</span>
            <StatusDot
              tone={endpointTone(data.state, data.online)}
              label={offline ? "offline" : data.state}
              pulse={offline}
            />
          </span>
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span>
              {data.hw_manufacturer} {data.hw_model}
            </span>
            <span>{data.site_name ?? "no site assigned"}</span>
            {data.reseller_name && <span>{data.reseller_name}</span>}
            <span className="font-mono text-xs">{data.id}</span>
          </span>
        }
        actions={<Actions endpoint={data} me={me} onDone={refresh} />}
      />

      <nav className="mb-4 flex gap-1 border-b border-ink-600">
        {tabs.map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={
              tab === key
                ? "border-b-2 border-brand px-4 py-2 text-sm font-medium text-ink-100"
                : "border-b-2 border-transparent px-4 py-2 text-sm text-ink-400 hover:text-ink-200"
            }
          >
            {label}
            {key === "vitals" && worstFault && (
              <span
                className={`ml-2 inline-block h-1.5 w-1.5 rounded-full align-middle ${
                  worstFault === "crit" ? "bg-state-crit" : "bg-state-warn"
                }`}
                aria-label={`${faults.length} issue${faults.length === 1 ? "" : "s"} need attention`}
              />
            )}
          </button>
        ))}
      </nav>

      {tab === "status" && <StatusTab endpoint={data} />}
      {tab === "vitals" && (
        <EndpointVitals telemetry={telemetry} checkinAgo={data.seconds_since_checkin} />
      )}
      {tab === "hardware" && <HardwareTab endpoint={data} />}
      {tab === "config" && <ConfigTab endpoint={data} me={me} onDone={refresh} />}
      {tab === "commands" && <CommandsTab endpoint={data} me={me} />}
      {tab === "history" && <HistoryTab endpoint={data} />}
    </>
  );
}

// --- status ----------------------------------------------------------------
function StatusTab({ endpoint }: { endpoint: Detail }) {
  const sources = endpoint.health_json.sources ?? [];
  const outputs = endpoint.health_json.outputs ?? [];
  const wg = endpoint.health_json.wireguard;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
        <Metric label="Last check-in" value={formatAgo(endpoint.seconds_since_checkin)} />
        <Metric label="Uptime" value={formatDuration(endpoint.uptime_s)} />
        <Metric label="CPU" value={endpoint.cpu_pct != null ? `${endpoint.cpu_pct}%` : "—"} />
        <Metric label="Memory" value={endpoint.mem_pct != null ? `${endpoint.mem_pct}%` : "—"} />
        <Metric label="Disk" value={endpoint.disk_pct != null ? `${endpoint.disk_pct}%` : "—"} />
        <Metric label="Temp" value={endpoint.temp_c ? `${endpoint.temp_c}°C` : "—"} />
      </div>

      <Card title="Tunnel and identity">
        <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm md:grid-cols-4">
          <Detail_ label="WireGuard">
            {wg?.up ? (
              <StatusDot tone="ok" label={`up · handshake ${formatAgo(wg.last_handshake_s)}`} />
            ) : (
              <StatusDot tone="crit" label="down" />
            )}
          </Detail_>
          <Detail_ label="Overlay address">
            <code className="font-mono text-xs">{endpoint.wg_ip ?? "—"}</code>
            {endpoint.wg_ip && (
              <div className="mt-1 text-xs text-ink-400">ssh outpost@{endpoint.wg_ip}</div>
            )}
          </Detail_>
          <Detail_ label="Media token">{endpoint.media_jwt_kid ?? "not issued"}</Detail_>
          <Detail_ label="Poll interval">{endpoint.poll_interval_s ?? 30}s</Detail_>
        </dl>
      </Card>

      <Card
        title="Per-channel source health"
        actions={
          endpoint.bandwidth_estimate && (
            <span className="text-xs text-ink-400">
              estimated WAN {endpoint.bandwidth_estimate.estimated_mbps} Mbps
            </span>
          )
        }
        padded={false}
      >
        {sources.length === 0 ? (
          <Empty>No sources reported yet.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Channel</th>
                  <th>Profile</th>
                  <th>State</th>
                  <th>Origin</th>
                  <th>Segment fetch p95</th>
                  <th>Stalls 15m</th>
                  <th>Playlist age</th>
                  <th>Bitrate</th>
                </tr>
              </thead>
              <tbody>
                {sources.map((source) => (
                  <SourceRow key={source.channel_id} source={source} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Output health" padded={false}>
        {outputs.length === 0 ? (
          <Empty>No outputs running.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Output</th>
                  <th>Type</th>
                  <th>Channel</th>
                  <th>State</th>
                  <th>Bitrate</th>
                  <th>Restarts</th>
                </tr>
              </thead>
              <tbody>
                {outputs.map((output) => (
                  <tr key={output.output_id}>
                    <td className="font-mono text-xs">{output.output_id}</td>
                    <td className="uppercase">{output.type}</td>
                    <td>{output.channel_id ?? "all"}</td>
                    <td>
                      <StatusDot
                        tone={output.state === "running" ? "ok" : output.state === "stopped" ? "idle" : "crit"}
                        label={output.state ?? "unknown"}
                      />
                    </td>
                    <td className="tabular-nums">{output.out_mbps ? `${output.out_mbps} Mbps` : "—"}</td>
                    <td className="tabular-nums">{output.restarts ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function SourceRow({ source }: { source: SourceHealth }) {
  const good = source.state === "receiving";
  const slow = (source.seg_fetch_ms_p95 ?? 0) > 1500;
  return (
    <tr>
      <td>
        {source.number ? `${source.number} · ` : ""}
        {source.name ?? source.channel_id}
      </td>
      <td className="uppercase">{source.profile ?? "—"}</td>
      <td>
        <StatusDot tone={good ? "ok" : "crit"} label={source.state ?? "unknown"} />
      </td>
      <td>
        {source.origin === "backup" ? (
          <Badge tone="warn">backup</Badge>
        ) : (
          <span className="text-ink-400">primary</span>
        )}
      </td>
      <td className={slow ? "tabular-nums text-state-warn" : "tabular-nums"}>
        {source.seg_fetch_ms_p95 != null ? `${Math.round(source.seg_fetch_ms_p95)} ms` : "—"}
      </td>
      <td className={source.stalls_15m ? "tabular-nums text-state-warn" : "tabular-nums"}>
        {source.stalls_15m ?? 0}
      </td>
      <td className="tabular-nums">{source.playlist_age_s != null ? `${source.playlist_age_s}s` : "—"}</td>
      <td className="tabular-nums">{source.mbps ? `${source.mbps} Mbps` : "—"}</td>
    </tr>
  );
}

// --- hardware --------------------------------------------------------------
function HardwareTab({ endpoint }: { endpoint: Detail }) {
  const hw = endpoint.hardware_json ?? {};
  const roles = endpoint.nic_roles_json ?? {};
  const { data: history } = useQuery<Page<{ id: number; hardware_hash: string; change_summary: string | null; at: string }>>({
    queryKey: ["endpoint", endpoint.id, "hardware_history"],
    queryFn: () => api.get(`/v1/endpoints/${endpoint.id}/hardware_history`),
  });

  const roleFor = (mac?: string) => {
    if (!mac) return null;
    if (mac.toLowerCase() === roles.wan?.mac?.toLowerCase()) return "WAN";
    if (mac.toLowerCase() === roles.lan?.mac?.toLowerCase()) return "LAN";
    return null;
  };

  return (
    <div className="space-y-6">
      {endpoint.capability && endpoint.capability.warnings.length > 0 && (
        <div className="rounded-md border border-state-warn/40 bg-state-warn/10 p-4 text-sm text-state-warn">
          <div className="font-medium">Capability check</div>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            {endpoint.capability.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="System">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Detail_ label="Manufacturer">{hw.system?.manufacturer ?? "—"}</Detail_>
            <Detail_ label="Model">{hw.system?.model ?? "—"}</Detail_>
            <Detail_ label="Service tag">
              <span className="service-tag">{hw.system?.service_tag ?? endpoint.service_tag}</span>
            </Detail_>
            <Detail_ label="BIOS">{hw.system?.bios ?? "—"}</Detail_>
            <Detail_ label="Inventory hash">
              <code className="font-mono text-xs">{endpoint.hardware_hash?.slice(7, 19) ?? "—"}</code>
            </Detail_>
            <Detail_ label="Collected">{formatTime(endpoint.hardware_updated_at)}</Detail_>
          </dl>
        </Card>

        <Card title="CPU and memory">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Detail_ label="CPU">{hw.cpu?.model ?? "—"}</Detail_>
            <Detail_ label="Topology">
              {hw.cpu?.sockets ?? "?"} socket · {hw.cpu?.cores ?? "?"} cores · {hw.cpu?.threads ?? "?"} threads
            </Detail_>
            <Detail_ label="Memory">
              {hw.memory?.total_mb ? `${Math.round(hw.memory.total_mb / 1024)} GB` : "—"}
            </Detail_>
            <Detail_ label="DIMMs">{hw.memory?.dimms?.length ?? 0} populated</Detail_>
            {endpoint.capability && (
              <Detail_ label="Estimated QAM capacity">
                {endpoint.capability.estimated_qam_channels} hd channels
                <span className="ml-1 text-xs text-ink-400">
                  ({endpoint.capability.cores_per_channel} cores each)
                </span>
              </Detail_>
            )}
            <Detail_ label="Assist GPU">
              {hw.gpus?.length ? hw.gpus.map((g) => g.model).join(", ") : "none"}
            </Detail_>
          </dl>
        </Card>
      </div>

      <Card title="Network interfaces" padded={false}>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Interface</th>
                <th>MAC</th>
                <th>Role</th>
                <th>Speed</th>
                <th>Link</th>
                <th>Addresses</th>
                <th>Driver</th>
              </tr>
            </thead>
            <tbody>
              {(hw.nics ?? []).map((nic) => (
                <tr key={nic.mac}>
                  <td className="font-mono text-xs">{nic.name}</td>
                  <td className="font-mono text-xs">{nic.mac}</td>
                  <td>
                    {roleFor(nic.mac) ? (
                      <Badge tone="brand">{roleFor(nic.mac)}</Badge>
                    ) : (
                      <span className="text-ink-400">—</span>
                    )}
                  </td>
                  <td className="tabular-nums">{nic.speed_mbps ? `${nic.speed_mbps} Mbps` : "—"}</td>
                  <td>
                    <StatusDot tone={nic.link ? "ok" : "idle"} label={nic.link ? "up" : "down"} />
                  </td>
                  <td className="font-mono text-xs">{nic.addrs?.join(", ") || "—"}</td>
                  <td className="text-ink-400">{nic.driver ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="Storage" padded={false}>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Device</th>
                <th>Model</th>
                <th>Serial</th>
                <th>Size</th>
                <th>Bus</th>
                <th>SMART</th>
              </tr>
            </thead>
            <tbody>
              {(hw.storage ?? []).map((disk) => (
                <tr key={disk.serial ?? disk.dev}>
                  <td className="font-mono text-xs">{disk.dev}</td>
                  <td>{disk.model}</td>
                  <td className="font-mono text-xs text-ink-400">{disk.serial}</td>
                  <td className="tabular-nums">{disk.size_gb} GB</td>
                  <td className="uppercase text-ink-400">{disk.bus}</td>
                  <td>
                    {disk.smart_ok === true ? (
                      <StatusDot tone="ok" label="ok" />
                    ) : disk.smart_ok === false ? (
                      <StatusDot tone="crit" label="failing" />
                    ) : (
                      <StatusDot tone="idle" label="unknown" />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="Inventory change history" padded={false}>
        {!history?.items.length ? (
          <Empty>No inventory changes recorded.</Empty>
        ) : (
          <ul className="divide-y divide-ink-700">
            {history.items.map((entry) => (
              <li key={entry.id} className="flex items-baseline gap-4 px-4 py-3 text-sm">
                <span className="w-40 shrink-0 text-ink-400">{formatTime(entry.at)}</span>
                <span className={entry.change_summary === "initial inventory" ? "text-ink-400" : "text-ink-100"}>
                  {entry.change_summary ?? "no change recorded"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

// --- config ----------------------------------------------------------------
function ConfigTab({ endpoint, me, onDone }: { endpoint: Detail; me: Me; onDone: () => void }) {
  const [editingRoles, setEditingRoles] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [editingOutputs, setEditingOutputs] = useState(false);
  const roles = endpoint.nic_roles_json ?? {};
  const canEdit = me.role !== "reseller" || endpoint.reseller_id === me.reseller_id;

  return (
    <div className="space-y-6">
      <Card
        title="NIC roles"
        actions={
          me.role !== "reseller" && (
            <button className="btn-secondary" onClick={() => setEditingRoles(true)}>
              Assign roles
            </button>
          )
        }
      >
        <p className="mb-4 text-xs text-ink-400">
          Roles are pinned to MAC addresses, never to interface names. The agent resolves each MAC to its
          current interface at apply time; if a role&rsquo;s MAC goes missing it holds the last-good network
          config and raises an alert rather than guessing.
        </p>
        <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm md:grid-cols-4">
          <Detail_ label="WAN">
            <code className="font-mono text-xs">{roles.wan?.mac ?? "not assigned"}</code>
          </Detail_>
          <Detail_ label="LAN">
            <code className="font-mono text-xs">{roles.lan?.mac ?? "not assigned"}</code>
          </Detail_>
          <Detail_ label="LAN addressing">
            {roles.lan?.mode === "dhcp" ? "DHCP" : (roles.lan?.addr ?? "—")}
          </Detail_>
          <Detail_ label="LAN gateway">{roles.lan?.gateway ?? "—"}</Detail_>
        </dl>
      </Card>

      <Card
        title={`Channels (${endpoint.entitlements.length})`}
        actions={
          canEdit && (
            <button className="btn-primary" onClick={() => setProvisioning(true)}>
              {endpoint.entitlements.length ? "Edit service" : "Provision"}
            </button>
          )
        }
        padded={false}
      >
        {endpoint.entitlements.length === 0 ? (
          <Empty>No channels provisioned.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Channel</th>
                  <th>Profile</th>
                  <th>Nominal bitrate</th>
                  <th>Since</th>
                </tr>
              </thead>
              <tbody>
                {endpoint.entitlements.map((entitlement) => (
                  <tr key={entitlement.id}>
                    <td className="tabular-nums">{entitlement.channel_number}</td>
                    <td>{entitlement.channel_name}</td>
                    <td className="uppercase">{entitlement.profile}</td>
                    <td className="tabular-nums">
                      {entitlement.nominal_kbps ? `${entitlement.nominal_kbps} kbps` : "—"}
                    </td>
                    <td className="text-ink-400">{formatTime(entitlement.added_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card
        title={`Outputs (${endpoint.outputs.length})`}
        padded={false}
        actions={
          // Editing outputs no longer means re-running the provisioning wizard,
          // which would also reset the site and the channel set.
          endpoint.entitlements.length > 0 && (
            <button className="btn-secondary" onClick={() => setEditingOutputs(true)}>
              Edit outputs
            </button>
          )
        }
      >
        {endpoint.outputs.length === 0 ? (
          <Empty>No outputs configured.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Channel</th>
                  <th>Parameters</th>
                  <th>Enabled</th>
                </tr>
              </thead>
              <tbody>
                {endpoint.outputs.map((output) => (
                  <tr key={output.id}>
                    <td className="uppercase">{output.type}</td>
                    <td>{output.channel_name ?? "all channels"}</td>
                    <td className="font-mono text-xs text-ink-300">{describeOutput(output.params_json)}</td>
                    <td>
                      <StatusDot tone={output.enabled ? "ok" : "idle"} label={output.enabled ? "yes" : "no"} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editingRoles && (
        <NicRoleModal endpoint={endpoint} onClose={() => setEditingRoles(false)} onDone={onDone} />
      )}
      {editingOutputs && (
        <OutputsModal
          endpoint={endpoint}
          onClose={() => setEditingOutputs(false)}
          onDone={() => {
            setEditingOutputs(false);
            onDone();
          }}
        />
      )}
      {provisioning && (
        <ProvisionWizard
          endpoint={endpoint}
          onClose={() => setProvisioning(false)}
          onDone={() => {
            setProvisioning(false);
            onDone();
          }}
        />
      )}
    </div>
  );
}

function describeOutput(params: Record<string, unknown>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== null && v !== "" && v !== undefined);
  return entries.map(([k, v]) => `${k}=${k.includes("pass") ? "•••" : v}`).join(" ") || "—";
}

export function NicRoleModal({
  endpoint,
  onClose,
  onDone,
}: {
  endpoint: Detail;
  onClose: () => void;
  onDone: () => void;
}) {
  const nics = endpoint.hardware_json?.nics ?? [];
  const suggestedWan = nics.find((n) => n.default_route)?.mac ?? endpoint.nic_roles_json?.wan?.mac ?? "";
  const [wan, setWan] = useState(suggestedWan);
  const [lan, setLan] = useState(endpoint.nic_roles_json?.lan?.mac ?? "");
  const [mode, setMode] = useState(endpoint.nic_roles_json?.lan?.mode ?? "static");
  const [addr, setAddr] = useState(endpoint.nic_roles_json?.lan?.addr ?? "");
  const [gateway, setGateway] = useState(endpoint.nic_roles_json?.lan?.gateway ?? "");
  const { run, busy } = useAction();

  const options = nics.map((nic) => (
    <option key={nic.mac} value={nic.mac}>
      {nic.name} · {nic.mac} · {nic.speed_mbps ? `${nic.speed_mbps} Mbps` : "unknown speed"} ·{" "}
      {nic.link ? "up" : "down"}
      {nic.default_route ? " · default route" : ""}
    </option>
  ));

  return (
    <Modal
      title="Assign NIC roles"
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={busy || !wan || !lan}
            onClick={() =>
              run(async () => {
                await api.patch(`/v1/endpoints/${endpoint.id}/nic_roles`, {
                  wan_mac: wan,
                  lan_mac: lan,
                  lan_mode: mode,
                  lan_addr: mode === "static" ? addr : null,
                  lan_gateway: mode === "static" ? gateway || null : null,
                });
                onDone();
                onClose();
              }, "NIC roles assigned — a new config version is pending")
            }
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="WAN — control and media pull" hint="Pre-suggested: the interface holding the default route.">
          <select className="input" value={wan} onChange={(e) => setWan(e.target.value)}>
            <option value="">Select an interface…</option>
            {options}
          </select>
        </Field>
        <Field label="LAN — QAM multicast, local HLS, SRT listener">
          <select className="input" value={lan} onChange={(e) => setLan(e.target.value)}>
            <option value="">Select an interface…</option>
            {options}
          </select>
        </Field>
        <Field label="LAN addressing">
          <select className="input" value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="static">Static</option>
            <option value="dhcp">DHCP</option>
          </select>
        </Field>
        {mode === "static" && (
          <div className="grid grid-cols-2 gap-4">
            <Field label="LAN address (CIDR)">
              <input className="input" placeholder="10.50.0.2/24" value={addr} onChange={(e) => setAddr(e.target.value)} />
            </Field>
            <Field label="LAN gateway (optional)" hint="Never becomes the default route.">
              <input className="input" placeholder="10.50.0.1" value={gateway} onChange={(e) => setGateway(e.target.value)} />
            </Field>
          </div>
        )}
        <p className="text-xs text-ink-400">
          The agent manages LAN addressing only. It never reconfigures the WAN interface remotely — an
          Outpost has no inbound path, so a bad WAN change would need a site visit to undo.
        </p>
      </div>
    </Modal>
  );
}

// --- commands --------------------------------------------------------------
const COMMANDS: { type: string; label: string; nciOnly?: boolean; needs?: "channel" | "service" }[] = [
  { type: "resync_config", label: "Resync config" },
  { type: "restart_all", label: "Restart all outputs" },
  { type: "restart_output", label: "Restart one channel", needs: "channel" },
  { type: "source_flip", label: "Flip origin for a channel", needs: "channel" },
  { type: "channel_scan_report", label: "Channel scan report" },
  { type: "pull_logs", label: "Pull logs", nciOnly: true, needs: "service" },
  { type: "speedtest", label: "Speed test", nciOnly: true },
  { type: "wg_rekey", label: "Rekey WireGuard", nciOnly: true },
  { type: "reboot", label: "Reboot", nciOnly: true },
];

function CommandsTab({ endpoint, me }: { endpoint: Detail; me: Me }) {
  const queryClient = useQueryClient();
  const [param, setParam] = useState<Record<string, string>>({});
  const { run, busy } = useAction();

  const { data } = useQuery<Page<Command>>({
    queryKey: ["endpoint", endpoint.id, "commands"],
    queryFn: () => api.get(`/v1/endpoints/${endpoint.id}/commands`),
    refetchInterval: 15_000,
  });

  const send = (type: string, params: Record<string, unknown>) =>
    run(async () => {
      await api.post(`/v1/endpoints/${endpoint.id}/commands`, { type, params });
      queryClient.invalidateQueries({ queryKey: ["endpoint", endpoint.id, "commands"] });
    }, `${type} queued — it runs on the next check-in`);

  return (
    <div className="space-y-6">
      <Card title="Send a command">
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {COMMANDS.filter((c) => !c.nciOnly || me.role !== "reseller").map((command) => (
            <div key={command.type} className="rounded-md border border-ink-600 p-3">
              <div className="text-sm font-medium">{command.label}</div>
              {command.needs === "channel" && (
                <select
                  className="input mt-2"
                  value={param[command.type] ?? ""}
                  onChange={(e) => setParam({ ...param, [command.type]: e.target.value })}
                >
                  <option value="">Choose a channel…</option>
                  {endpoint.entitlements.map((entitlement) => (
                    <option key={entitlement.channel_id} value={entitlement.channel_id}>
                      {entitlement.channel_number} · {entitlement.channel_name}
                    </option>
                  ))}
                </select>
              )}
              {command.needs === "service" && (
                <input
                  className="input mt-2"
                  placeholder="hls-rx-101"
                  value={param[command.type] ?? ""}
                  onChange={(e) => setParam({ ...param, [command.type]: e.target.value })}
                />
              )}
              <button
                className="btn-secondary mt-2 w-full"
                disabled={busy || (!!command.needs && !param[command.type])}
                onClick={() =>
                  send(
                    command.type,
                    command.needs === "channel"
                      ? { channel_id: Number(param[command.type]) }
                      : command.needs === "service"
                        ? { service: param[command.type], lines: 500 }
                        : {},
                  )
                }
              >
                Send
              </button>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Command log" padded={false}>
        {!data?.items.length ? (
          <Empty>No commands sent.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Queued</th>
                  <th>Command</th>
                  <th>By</th>
                  <th>State</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((command) => (
                  <tr key={command.id}>
                    <td className="text-ink-400">{formatTime(command.created_at)}</td>
                    <td className="font-mono text-xs">{command.type}</td>
                    <td className="text-ink-400">{command.created_by ?? "—"}</td>
                    <td>
                      <StatusDot
                        tone={
                          command.state === "succeeded"
                            ? "ok"
                            : command.state === "failed"
                              ? "crit"
                              : command.state === "delivered"
                                ? "info"
                                : "idle"
                        }
                        label={command.state}
                      />
                    </td>
                    <td className="max-w-md truncate font-mono text-xs text-ink-400">
                      {command.result_json ? JSON.stringify(command.result_json).slice(0, 160) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// --- history ---------------------------------------------------------------
function HistoryTab({ endpoint }: { endpoint: Detail }) {
  const { data } = useQuery<
    Page<{
      id: number;
      version_n: number;
      created_by: string | null;
      reason: string | null;
      created_at: string;
      applied_at: string | null;
      apply_result: { ok?: boolean; detail?: string } | null;
    }>
  >({
    queryKey: ["endpoint", endpoint.id, "config_versions"],
    queryFn: () => api.get(`/v1/endpoints/${endpoint.id}/config_versions`),
  });

  return (
    <Card title="Config versions" padded={false}>
      {!data?.items.length ? (
        <Empty>No config versions yet.</Empty>
      ) : (
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Version</th>
                <th>Created</th>
                <th>By</th>
                <th>Reason</th>
                <th>Applied</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((version) => (
                <tr key={version.id}>
                  <td className="tabular-nums font-medium">v{version.version_n}</td>
                  <td className="text-ink-400">{formatTime(version.created_at)}</td>
                  <td className="text-ink-400">{version.created_by ?? "—"}</td>
                  <td>{version.reason ?? "—"}</td>
                  <td className="text-ink-400">{formatTime(version.applied_at)}</td>
                  <td>
                    {version.apply_result ? (
                      <StatusDot
                        tone={version.apply_result.ok ? "ok" : "crit"}
                        label={version.apply_result.ok ? "applied" : (version.apply_result.detail ?? "failed")}
                      />
                    ) : version.applied_at ? (
                      <StatusDot tone="ok" label="applied" />
                    ) : (
                      <StatusDot tone="info" label="pending" />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// --- actions ---------------------------------------------------------------
function Actions({ endpoint, me, onDone }: { endpoint: Detail; me: Me; onDone: () => void }) {
  const { run } = useAction();
  const isAdmin = me.role === "nci_admin";

  const call = (path: string, message: string) =>
    run(async () => {
      await api.post(`/v1/endpoints/${endpoint.id}/${path}`);
      onDone();
    }, message);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {me.role !== "reseller" && (
        <button className="btn-secondary" onClick={() => call("resync", "Config rebuilt")}>
          Rebuild config
        </button>
      )}
      {isAdmin && endpoint.state === "suspended" && (
        <ConfirmButton label="Resume" onConfirm={() => call("resume", "Outpost resumed")} />
      )}
      {isAdmin && endpoint.state === "active" && (
        <ConfirmButton
          label="Suspend"
          confirmLabel="Suspend — click again to stop this site's streams"
          onConfirm={() => call("suspend", "Outpost suspended — streams stop within the auth cache TTL")}
        />
      )}
      {isAdmin && endpoint.state !== "revoked" && (
        <ConfirmButton
          danger
          label="Revoke"
          confirmLabel="Revoke — click again to kill this box permanently"
          onConfirm={() => call("revoke", "Outpost revoked — its certificate, WG key and media token are void")}
        />
      )}
    </div>
  );
}

function Detail_({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-ink-400">{label}</dt>
      <dd className="mt-0.5 text-ink-100">{children}</dd>
    </div>
  );
}

/** Change delivery without re-running provisioning.
 *
 *  This is the same editor the provisioning wizard uses — there is one place
 *  where an SRT target or a QAM plan is described, and it would drift if there
 *  were two. The difference is what it submits: PATCH /outputs touches outputs
 *  and nothing else, so the site, the channel set and the entitlement ledger
 *  are all left alone. Outputs are part of the signed config document, so
 *  saving mints a new version and the Outpost picks it up at its next check-in
 *  — within about half a minute.
 */
function OutputsModal({
  endpoint,
  onClose,
  onDone,
}: {
  endpoint: Detail;
  onClose: () => void;
  onDone: () => void;
}) {
  const [outputs, setOutputs] = useState<OutputSpec[]>(
    endpoint.outputs.map((o) => ({
      type: o.type,
      channel_id: o.channel_id,
      params: o.params_json,
      enabled: o.enabled,
    })),
  );
  const { run, busy } = useAction();

  const { data: channels } = useQuery<Page<Channel>>({
    queryKey: ["channels"],
    queryFn: () => api.get("/v1/channels?status=active"),
  });

  // Only channels this Outpost is actually entitled to can carry an output —
  // the API rejects anything else, so the editor should not offer it.
  const entitled = new Set(endpoint.entitlements.map((e) => e.channel_id));
  const catalog = (channels?.items ?? []).filter((c) => entitled.has(c.id));
  const selected = endpoint.entitlements.map((e) => e.channel_id);

  return (
    <Modal
      title="Edit outputs"
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                await api.patch(`/v1/endpoints/${endpoint.id}/outputs`, { outputs });
                onDone();
              }, "Outputs saved. The Outpost applies them at its next check-in.")
            }
          >
            {busy ? "Saving…" : "Save outputs"}
          </button>
        </>
      }
    >
      <div className="callout mb-4">
        Channels and profile are set under <strong>Edit service</strong>. This changes only how the
        Outpost delivers them.
      </div>
      <OutputsStep outputs={outputs} setOutputs={setOutputs} selected={selected} catalog={catalog} />
    </Modal>
  );
}

export type { Channel, Site };
